import React, { memo } from 'react';
import { Box, Flex, Text, VStack } from '@chakra-ui/react';

/**
 * @param {object} props
 * @param {number} props.x
 * @param {number} props.o
 * @param {number} props.draws
 * @param {'X' | 'O' | null} props.leader
 */
function TicTacToeScoreboard({ x, o, draws, leader, fillParent = false }) {
  const StatRow = ({ label, value, accent, emphasize }) => {
    const barColor =
      accent === 'x' ? 'cyan.400' : accent === 'o' ? 'orange.400' : 'gray.300';

    return (
      <Flex
        align="center"
        justify="space-between"
        gap={3}
        px={4}
        py={4}
        borderRadius="xl"
        borderWidth="0"
        bg={emphasize ? 'rgba(255, 255, 255, 0.92)' : 'rgba(255, 255, 255, 0.55)'}
        position="relative"
        overflow="hidden"
        transition="box-shadow 0.2s ease, background 0.2s ease"
        boxShadow={
          emphasize
            ? '0 0 0 1px rgba(13, 148, 136, 0.12), 0 2px 8px rgba(13, 148, 136, 0.08), 0 0 24px -8px rgba(45, 212, 191, 0.15)'
            : '0 1px 2px rgba(15, 23, 42, 0.04)'
        }
        _before={{
          content: '""',
          position: 'absolute',
          left: 0,
          top: 3,
          bottom: 3,
          w: '3px',
          borderRadius: 'full',
          bg: barColor,
          opacity: emphasize ? 1 : 0.45,
        }}
        sx={{ pl: 5 }}
      >
        <Box minW={0}>
          <Text
            fontSize="9px"
            fontWeight="700"
            color="gray.400"
            textTransform="uppercase"
            letterSpacing="0.12em"
            lineHeight={1.3}
          >
            {label}
          </Text>
        </Box>
        <Text
          fontSize="4xl"
          fontWeight="800"
          letterSpacing="-0.04em"
          lineHeight={1}
          color={
            accent === 'x' ? 'cyan.700' : accent === 'o' ? 'orange.700' : 'gray.600'
          }
          fontVariantNumeric="tabular-nums"
        >
          {value}
        </Text>
      </Flex>
    );
  };

  return (
    <Box
      h={fillParent ? '100%' : 'auto'}
      flex={fillParent ? 1 : undefined}
      display="flex"
      flexDirection="column"
      bg="linear-gradient(180deg, rgba(241, 245, 249, 0.9) 0%, rgba(248, 250, 252, 0.95) 100%)"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="2xl"
      overflow="hidden"
      boxShadow="0 1px 3px rgba(15, 23, 42, 0.05), inset 0 1px 0 rgba(255,255,255,0.7)"
    >
      <Box px={5} pt={5} pb={0.5}>
        <Text
          as="h2"
          fontSize="10px"
          fontWeight="800"
          color="gray.500"
          textTransform="uppercase"
          letterSpacing="0.14em"
        >
          Session scores
        </Text>
        <Text fontSize="10px" color="gray.300" mt={1.5} lineHeight="short" fontWeight="500">
          Stored locally in your browser
        </Text>
      </Box>
      <VStack
        spacing={2}
        align="stretch"
        px={3.5}
        pb={5}
        pt={4}
        flex={fillParent ? 1 : undefined}
        justify={fillParent ? 'space-evenly' : undefined}
      >
        <StatRow label="X wins" value={x} accent="x" emphasize={leader === 'X'} />
        <StatRow label="Draws" value={draws} accent={null} emphasize={false} />
        <StatRow label="O wins" value={o} accent="o" emphasize={leader === 'O'} />
      </VStack>
    </Box>
  );
}

export default memo(TicTacToeScoreboard);
